/**
 * Runtime app configuration.
 *
 * Deployed builds fetch `/config.json` (a static file written per environment),
 * so a single build artifact can point at prod or dev without rebuilding. Local
 * `vite dev` skips the fetch and reads import.meta.env (the direnv profile)
 * instead, so local development is unchanged.
 *
 * `loadConfig()` must resolve before anything reads `getConfig()` — main.tsx
 * awaits it, then dynamically imports the app, so module-scope reads (e.g. the
 * platform client) only evaluate after the config is in place.
 *
 * The fetch/merge/cache loader mechanism lives in `@foir/data`'s
 * `createConfigLoader`; here we define this app's shape + build-time fallback +
 * the runtime-origin transform.
 */

import { createConfigLoader } from '@foir/data';

export interface AppConfig {
  /** Go platform base URL (ConnectRPC + REST, incl. /oauth/authorize). */
  platformUrl: string;
  /**
   * The data API's base URL. The finishing halves of signing in that a page
   * here posts a proof to (a reset link's token) are its routes.
   */
  publicApiUrl: string;
  /** auth.foir.dev — where login/signup redirects go. */
  authUrl: string;
  /**
   * Fóir's own login host, as the environment sets it. `authUrl` is pinned to
   * whatever origin serves this bundle, which on a project's own login domain
   * is the project's; this is where the pages of Fóir's console stay, and
   * where that domain sends somebody who reaches one.
   */
  foirAuthUrl: string;
  /** Default app to land on after login when no valid returnTo is supplied. */
  consoleUrl: string;
  /**
   * The account console app (account.foir.io). Fresh self-service signups land
   * here (the new owner's workspace), not in the content editor.
   */
  accountUrl: string;
  /**
   * staff.foir.io — the staff console. An approved post-login returnTo
   * origin so a staff member sent to log in from the staff console lands back
   * there.
   */
  staffUrl: string;
  /** The deployed environment ("development", "production") errors name. */
  environment: string;
  /** PostHog project API key (public — ships in the bundle). Empty disables product analytics. */
  posthogKey: string;
  /** PostHog ingestion host (EU cloud). */
  posthogHost: string;
}

const fallback: AppConfig = {
  platformUrl: import.meta.env.VITE_PLATFORM_URL || 'http://localhost:4011',
  publicApiUrl: import.meta.env.VITE_PUBLIC_API_URL || 'http://localhost:4002',
  authUrl: import.meta.env.VITE_AUTH_URL || 'http://localhost:3003',
  foirAuthUrl: import.meta.env.VITE_AUTH_URL || 'http://localhost:3003',
  consoleUrl: import.meta.env.VITE_CONSOLE_URL || 'https://app.foir.io',
  accountUrl: import.meta.env.VITE_ACCOUNT_URL || 'http://localhost:3001',
  staffUrl: import.meta.env.VITE_STAFF_URL || 'http://localhost:3005',
  environment: import.meta.env.VITE_DEPLOY_ENV || 'local',
  posthogKey: import.meta.env.VITE_POSTHOG_KEY || '',
  posthogHost: import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com',
};

export const { loadConfig, getConfig } = createConfigLoader(fallback, {
  transform: withRuntimeOrigin,
});

/**
 * Pin `authUrl` to the origin actually serving this SPA. The portal *is* the
 * auth app, so its own login/signup links must point back at whatever host it
 * was loaded from — `auth.foir.dev` OR a white-label custom login domain
 * (`login.<user>`), where the Caddy edge serves this same bundle. The
 * environment's own `authUrl` is kept as `foirAuthUrl`, where the pages of
 * Fóir's console are served. How the platform is reached from each host is the
 * platform client's rule (lib/platform-client.ts).
 */
function withRuntimeOrigin(cfg: AppConfig): AppConfig {
  if (typeof window === 'undefined') return cfg;
  return { ...cfg, foirAuthUrl: cfg.authUrl, authUrl: window.location.origin };
}
